import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Avatar,
} from "@mui/material";
import ExpandCircleDownIcon from "@mui/icons-material/ExpandCircleDown";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Loading from "../../../../common/loader/loading";
import CreateEditDialog from "../../../../common/dialog/create-edit-dialog";
import SupportStatementManager from "../managerLogs/supportStatementManager/support-statement-manager";
import DailyLogIncidentManager from "../managerLogs/dailyLogManager/daily-log-incident-manager";
import StaffDebriefManager from "../managerLogs/staffDebriefManager/staff-debrief-manager";
import ChildDebriefManager from "../managerLogs/childDebriefManager/child-debrief-manager";
import TasksExportIncident from "../exportManagerTable/export-options-incident";

const IncidentManagementView = () => {
  const [expandedRow, setExpandedRow] = useState(null); // State for expanded row
  const [loading, setLoading] = useState(true);
  const [filteredTasks, setFilteredTasks] = useState([]); // Tasks after filtering and sorting
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLabel, setDialogLabel] = useState(null);
  const [incidentId, setIncidentId] = useState("");
  const [supportStatement, setSupportStatement] = useState(null);
  const [staffDebrief, setStaffDebrief] = useState(null);
  const [childDebrief, setChildDebrief] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null); // State for selected task

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    if (!dateStr || isNaN(date.getTime())) return "Still not completed";

    return (
      date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }) +
      " " +
      date.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      })
    );
  };

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/dailyLogIncident/incidentAll");

      if (response.data) {
        setFilteredTasks(response.data);
      } else {
        console.error("Invalid response format:", response.data);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  useEffect(() => {
    const fetchSupportStatement = async () => {
      try {
        const response = await axios.get("/api/support/supportStatementAll");
        const matched = response?.data?.find((item) => item.incidentId === incidentId);
        setSupportStatement(matched || null);
      } catch (error) {
        console.error("Error fetching support:", error);
      } finally {
        setLoading(false);
      }
    };

    if (incidentId) {
      fetchSupportStatement();
    }
  }, [incidentId]);

  useEffect(() => {
    const fetchStaffDebrief = async () => {
      try {
        const response = await axios.get("/api/staff/staffDebriefAll");
        const matched = response?.data?.find((item) => item.incidentId === incidentId);
        setStaffDebrief(matched || null);
      } catch (error) {
        console.error("Error fetching support:", error);
      } finally {
        setLoading(false);
      }
    };

    if (incidentId) {
      fetchStaffDebrief();
    }
  }, [incidentId]);

  useEffect(() => {
    const fetchChildDebrief = async () => {
      try {
        const response = await axios.get("/api/child/childDebriefAll");
        const matched = response?.data?.find((item) => item.incidentId === incidentId);
        setChildDebrief(matched || null);
      } catch (error) {
        console.error("Error fetching support:", error);
      } finally {
        setLoading(false);
      }
    };

    if (incidentId) {
      fetchChildDebrief();
    }
  }, [incidentId]);

  if (loading) {
    return <Loading />;
  }

  return (
    <Grid container spacing={2}>
      <Grid item lg={9} md={8}>
        <Box sx={{ p: 4, ml: 2, mr: 2 }}>
          <Box display='flex' justifyContent='space-between' mb={2}>
            <Box>
              <Typography sx={{ fontSize: "24px", fontWeight: "bold", color: "black" }} align='left' mt={1}>
                Incident Management
              </Typography>
            </Box>
          </Box>

          {filteredTasks?.map((task) => (
            <Box key={task.id}>
              <Paper
                elevation={3}
                sx={{
                  padding: "20px 25px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  mb: "10px",
                }}
                onClick={() => {
                  setExpandedRow(expandedRow === task.id ? null : task.id);
                  setIncidentId(task?.id);
                  setSelectedTask(task);
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant='subtitle1' sx={{ fontWeight: "bold", fontSize: "14px" }}>
                    {String(task.id).padStart(2, "0")} Incident Management
                  </Typography>
                </Box>

                <Grid container alignItems='center' sx={{ flex: 1 }}>
                  <Grid item>
                    <Avatar alt='Profile' src='/images/person.png' sx={{ width: 40, height: 40, marginRight: 1 }} />
                  </Grid>
                  <Grid item>
                    <Typography variant='body2'>{formatDateTime(task?.updated_at)}</Typography>
                  </Grid>
                </Grid>

                <Box sx={{ textAlign: "right" }}>
                  <IconButton sx={{ color: "black" }}>
                    {expandedRow === task.id ? <ExpandLessIcon /> : <ExpandCircleDownIcon />}
                  </IconButton>
                </Box>
              </Paper>
              <Collapse in={expandedRow === task.id} timeout='auto' unmountOnExit>
                <Box sx={{ p: 1 }}>
                  <Table>
                    <TableBody>
                      {[
                        { label: "Incident Form", field: "updated_at" },
                        { label: "Child Debrief", field: "updated_at" },
                        { label: "Staff Debrief", field: "updated_at" },
                        { label: "Support Statement", field: "updated_at" },
                      ].map((item, idx) => (
                        <TableRow
                          key={idx}
                          onClick={() => {
                            setDialogOpen(true);
                            setDialogLabel(item.label);
                          }}
                        >
                          <TableCell sx={{ fontWeight: 500 }}>{item.label}</TableCell>
                          <TableCell>{formatDateTime(task[item?.field])}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
                {dialogOpen && dialogLabel === "Incident Form" ? (
                  <CreateEditDialog isOpen={dialogOpen} maxWidth='xl' onClose={() => setDialogOpen(false)}>
                    <DailyLogIncidentManager task={task} onClose={() => setDialogOpen(false)} />
                  </CreateEditDialog>
                ) : dialogLabel === "Support Statement" ? (
                  <CreateEditDialog isOpen={dialogOpen} maxWidth='xl' onClose={() => setDialogOpen(false)}>
                    <SupportStatementManager task={supportStatement} onClose={() => setDialogOpen(false)} />
                  </CreateEditDialog>
                ) : dialogLabel === "Staff Debrief" ? (
                  <CreateEditDialog isOpen={dialogOpen} maxWidth='xl' onClose={() => setDialogOpen(false)}>
                    <StaffDebriefManager task={staffDebrief} onClose={() => setDialogOpen(false)} />
                  </CreateEditDialog>
                ) : (
                  <CreateEditDialog isOpen={dialogOpen} maxWidth='xl' onClose={() => setDialogOpen(false)}>
                    <ChildDebriefManager task={childDebrief} onClose={() => setDialogOpen(false)} />
                  </CreateEditDialog>
                )}
              </Collapse>
            </Box>
          ))}
        </Box>
      </Grid>
      <Grid item lg={3} md={4}>
        <Box p={2} mt={5}>
          <TasksExportIncident
            filteredTasks={{
              ...selectedTask,
              staffDebrief,
              childDebrief,
              supportStatement,
            }}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

export default IncidentManagementView;
